import { LegacyProblemJsonSchema } from '@app/schemas/problem/legacy.problem.json.schema';
import { ProblemJsonSchema } from '@app/schemas/problem/problem.json.schema';

export type TExtractedProblemJson = {
  // The problem body exactly as received: members the schemas do not type (a dry-validation
  // `detail` array, `errors`, subscription extension members) are read from here.
  record: Record<string, unknown>;
  code: string | null;
  // A rollout-era body carried the machine code here instead of in `code`.
  title: string | null;
};

const _isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// The fetch wrappers keep the problem body raw (ProblemJsonHttpError, BulkAcceptHttpError), wrap it
// in `{ response: problem }` (PostHttpBadRequestError) or in `{ data: problem }` (Tagging controllers).
// A conformant body may itself carry a `response` or `data` object as an extension member, so the
// body is the first candidate and an envelope only wins when the body is not conformant.
const _conformantCandidates = (payload: Record<string, unknown>): Record<string, unknown>[] =>
  [payload, payload.response, payload.data].filter(_isRecord);

/**
 * The problem body of an HttpError payload, or null when the payload is no object. The record is
 * the first of the body, `response` or `data` that ProblemJsonSchema accepts. When none is
 * conformant (proxy errors, legacy controllers) the legacy order (response, data, body) is read
 * with LegacyProblemJsonSchema, where a malformed member reads as null.
 */
export const extractProblemJson = (payload: unknown): TExtractedProblemJson | null => {
  if (!_isRecord(payload)) return null;

  for (const candidate of _conformantCandidates(payload)) {
    const hardened = ProblemJsonSchema.safeParse(candidate);
    if (hardened.success) return { record: candidate, code: hardened.data.code, title: hardened.data.title };
  }

  const record = [payload.response, payload.data].find(_isRecord) ?? payload;
  const legacy = LegacyProblemJsonSchema.parse(record);

  return { record, code: legacy.code, title: legacy.title };
};
